<template>
  <div class="header-container">
    <div class="sticky-container">
      <v-row no-gutters>
        <!--SEARCH-->
        <v-col
          cols="12"
          md="auto"
          class="d-flex align-center gap"
          :class="{ 'flex-wrap': smAndDown }"
        >
          <div data-locator="documents-search">
            <SearchTextField
              v-model="search"
              color="border-primary"
              :disabled="actionsDisabled || (paginatedDocuments.length === 0 && !searchModeEnabled)"
              :tooltipText="$t('project.documents.DocumentsHeader.searchPlaceholder')"
            />
          </div>
          <app-button
            type="outlined"
            :disabled="actionsDisabled || paginatedDocuments.length === 0"
            variant="neutral"
            data-locator="filters-button"
            is-icon-color-custom
            @click="onOpenFiltersPane"
          >
            <template #left-icon>
              <app-material-icon name="instant_mix" />
            </template>
            {{ $t('common.filters') }}
          </app-button>
        </v-col>

        <v-col
          cols="12"
          md
          class="d-flex align-center gap"
          :class="smAndDown ? 'flex-wrap mt-4' : 'justify-end'"
        >
          <v-menu>
            <template #activator="{ props }">
              <app-button
                :disabled="actionsDisabled || paginatedDocuments.length === 0"
                type="outlined"
                variant="neutral"
                v-bind="props"
                is-icon-color-custom
                data-locator="options-menu-button"
              >
                {{ $t("project.documents.DocumentsHeader.options") }}
                <template #right-icon>
                  <app-material-icon name="keyboard_arrow_down" />
                </template>
              </app-button>
            </template>
            <v-list data-locator="documents-options-menu">
              <AppDownloadFile :link="exportIndexToExcelLink" class="d-block">
                <template #default="{ submitForm }">
                  <v-list-item @click="submitForm">
                    <v-list-item-title class="d-flex align-center" data-locator="excel-index-export-menu-item">
                      <app-material-icon name="file_export" color="inherit" class="mr-2 ml-1" />
                      <span class="text-text-secondary">{{ $t("project.documents.DocumentsHeader.exportIndexToExcel") }}</span>
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </AppDownloadFile>
              <AppDownloadFile :link="exportIndexToPdfLink" forceReader class="d-block">
                <template #default="{ submitForm }">
                  <v-list-item @click="submitForm">
                    <v-list-item-title class="d-flex align-center" data-locator="pdf-index-export-menu-item">
                      <app-material-icon name="file_export" class="mr-2 ml-1" />
                      <span class="text-text-secondary">{{ $t("project.documents.DocumentsHeader.exportIndexToPdf") }}</span>
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </AppDownloadFile>
              <v-list-item @click="onSendDocumentsIndexByEmail">
                <v-list-item-title class="d-flex align-center" data-locator="share-index-by-mail-menu-item">
                  <app-material-icon name="mail" class="mr-2 ml-1" />
                  <span class="text-text-secondary">{{ $t("project.documents.DocumentsHeader.sendIndexByEmail") }}</span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <template v-if="isDataRoom && currentRoom.isPM">
            <app-button
              type="outlined"
              variant="neutral"
              data-locator="access-rights-button"
              :to="{
                name: ROOM_SETTINGS_ROUTE_NAME,
                params: {
                  roomType: computedRoomRouteType,
                },
                state: {
                  goTo: '#accessRightsContainer',
                },
              }"
            >
              <template #left-icon>
                <app-material-icon name="lock" />
              </template>
              {{ $t('project.documents.DocumentsHeader.accessRightsButton') }}
            </app-button>
          </template>
          <template v-if="displayAddMenu">
            <AppTooltip v-if="currentUserRights.canUpload && hasTodoFolderOrChild"
                        :disabled="!showSizeExceededTooltip">
              <template #activator="{ props }">
                <!-- We bind the tooltip to the div, because it should be seen even with a disabled activator -->
                <!-- This makes it able to see the tooltip when disabled-->
                <div v-bind="props">
                  <app-button
                    :disabled="actionsDisabled || showSizeExceededTooltip"
                    data-locator="documents-add-file-button"
                    @click="OPEN_DOCUMENTS_UPLOAD_FILES"
                  >
                    <template #left-icon>
                      <app-material-icon name="add" color="icon-inverted" />
                    </template>
                    {{ $t("project.documents.DocumentsHeader.addFile") }}
                  </app-button>
                </div>
              </template>
              {{ $t('project.documents.DocumentsHeader.sizeExceededTooltip') }}
            </AppTooltip>
            <v-menu v-else :disabled="showSizeExceededTooltip" open-on-hover data-locator="documents-add-menu">
              <template v-slot:activator="{ props }">
                <!-- We bind the tooltip to the div, because it should be seen even with a disabled activator -->
                <!-- This makes it able to see the tooltip when disabled, but not the menu -->
                <!-- here use v-tooltip inside the v-menu because bug when use AppTooltip on v-menu -->
                <AppTooltip :disabled="!showSizeExceededTooltip">
                  <template #activator="{ props: tooltipProps }">
                    <div v-bind="tooltipProps">
                      <app-button :disabled="showSizeExceededTooltip" v-bind="props" data-locator="add-menu-button">
                        <template #left-icon>
                          <app-material-icon name="add" color="inherit" />
                        </template>
                        {{ $t("common.add") }}
                        <template #right-icon>
                          <app-material-icon name="keyboard_arrow_down" color="inherit" />
                        </template>
                      </app-button>
                    </div>
                  </template>
                  {{ $t('project.documents.DocumentsHeader.sizeExceededTooltip') }}
                </AppTooltip>
              </template>
              <v-list>
                <v-list-item
                  v-if="currentUserRights.canCreateFolders && !hasTodoFolderOrChild"
                  data-locator="add-file-or-folder-menu-item"
                  @click="OPEN_DOCUMENTS_UPLOAD_FILES"
                >
                  <v-list-item-title class="d-flex align-center">
                    <app-material-icon name="upload" class="mr-2" />
                    <span class="text-text-secondary">{{ $t("project.documents.DocumentsHeader.dropdownIcon.addFoldersOrFiles") }}</span>
                  </v-list-item-title>
                </v-list-item>

                <v-list-item
                  v-else-if="currentUserRights.canUpload && !isCurrentlyInTodoFolderRoot"
                  data-locator="add-file-menu-item"
                  @click="OPEN_DOCUMENTS_UPLOAD_FILES"
                >
                  <v-list-item-title class="d-flex align-center">
                    <app-material-icon name="upload" class="mr-2" />
                    <span class="text-text-secondary">{{ $t("project.documents.DocumentsHeader.dropdownIcon.addFiles") }}</span>
                  </v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="(currentRoom.isPM || currentUserRights.canCreateFolders) && !hasTodoFolderOrChild"
                  data-locator="new-folder-menu-item"
                  @click="OPEN_DOCUMENTS_CREATE_NEW_FOLDER"
                >
                  <v-list-item-title class="d-flex align-center">
                    <app-material-icon name="folder" class="mr-2" />
                    <span class="text-text-secondary">{{ $t("project.documents.DocumentsHeader.dropdownIcon.newFolder") }}</span>
                  </v-list-item-title>
                </v-list-item>
                <AppListDivider v-if="isExtraImportMenuDisplayed" />
                <v-list-item
                  v-if="isImportExistingTreeMenuDisplayed"
                  data-locator="import-existing-tree-menu-item"
                  @click="existingTreeDialogIsOpen = true"
                >
                  <v-list-item-title class="d-flex align-center">
                    <OfficeIcon
                      class="excel-icon mr-2"
                      document-type="excel"
                      :height="16"
                      icon-type="product"
                      :width="16"
                    />
                    <span class="text-text-secondary">{{ $t("project.documents.DocumentsHeader.dropdownIcon.existingTree") }}</span>
                  </v-list-item-title>
                </v-list-item>
                <PolyOfficeListItemImport
                  v-if="isImportFromPolyMenuDisplayed"
                  @polyOfficeOpenDialog="polyOfficeOpenDialogHandler"
                />
                <IManageListItemImport
                  v-if="imanageEnabledAndLogged"
                  :mnemo="mnemo"
                  @open-files-picker="openIManageFilesPicker"
                  @open-folders-picker="openIManageFoldersPicker"
                />
              </v-list>
            </v-menu>
          </template>
        </v-col>
      </v-row>
      <!--
        The margin is based on a v-row to avoid content margin calculation complexity
        while avoiding being a v-row and a flex container
      -->
      <div class="mt-3">
        <!-- We shouldn't show the breadcrumbs but we should still let it compute the paths -->
        <v-row class="pt-5 pl-3" v-show="!actionsDisabled">
        <!--
          The min width 0 classes are there to make sure that the flex elements shrink and
          allow space for the next column, enabling column-aware truncating
        -->
          <v-col class="d-flex align-center min-w-0">
            <DocumentsBreadcrumb
              v-if="!this.getFolderPathPending && !this.loading"
              v-show="breadcrumbIsShown"
              data-locator="documents-breadcrumb"
              class="flex-grow-1 min-w-0"
            />
            <v-skeleton-loader v-else width="260px" type="text" />
          </v-col>
          <v-col
            v-if="paginatedDocuments.length !== 0 && currentRoom.isPM && !hasTodoFolderOrChild && !hasQaFolderOrChild"
            cols="12"
            md="auto"
            class="ml-3 ml-md-0"
          >
            <AppTooltip>
              <template #activator="{ props }">
                <div v-bind="props">
                  <app-toggle
                    :value="numberingEnabled"
                    :loading="patchNumberingPending"
                    :label="$t('project.documents.DocumentsHeader.numbering')"
                    hide-details
                    data-locator="numbering-toggle"
                    :data-toggle-state="numberingEnabled? 'enabled' : 'disabled'"
                    @input="onNumberingChange()"
                  />
                </div>
              </template>
              <span>{{ $t("project.documents.DocumentsHeader.numberingTooltip") }}</span>
            </AppTooltip>
          </v-col>
        </v-row>
      </div>
      <v-row v-if="selectedDocuments.length > 0 && !loading" class="sticky-box">
        <v-col>
          <DocumentsActionBar class="mb-3"/>
        </v-col>
      </v-row>
    </div>

    <DocumentsHeaderFilters v-if="filtersCount > 0"/>

    <DocumentsExistingTreeDialog v-model:is-open="existingTreeDialogIsOpen" :mnemo="mnemo"/>
    <DocumentsNumberingDisableDialog
      v-if="showNumberingDisableDialog"
      :mnemo="mnemo"
      @confirm="confirmNumberingDisableDialog"
      @close="showNumberingDisableDialog = false"
    />

    <PolyOfficeDialog v-if="polyOfficeEnabled && dialogIsOpen" @callback="polyOfficeDialogCallback"/>

    <IManageFilePickerDialog
      v-model:is-open="iManageFilesPickerDialogIsOpen"
      @files="onImanageFiles"
    />
    <IManageFolderPickerDialog
      v-if="iManageFoldersPickerDialogIsOpen"
      v-model:is-open="iManageFoldersPickerDialogIsOpen"
      @folders="onImanageFolders"
    />
  </div>
</template>

<script>
import { debounce } from 'lodash-es'
import { useDisplay } from 'vuetify'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppListDivider from '@/common/AppListDivider.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import IManageFilePickerDialog from '@/common/imanage/IManageFilePickerDialog.vue'
import IManageFolderPickerDialog from '@/common/imanage/IManageFolderPickerDialog.vue'
import IManageListItemImport from '@/common/imanage/IManageListItemImport.vue'
import OfficeIcon from '@/common/office/OfficeIcon.vue'
import { DocumentsImport } from '@/common/polyOffice/polyOffice.model'
import PolyOfficeDialog, { POLY_OFFICE_DIALOG_STATUS_SUCCESS } from '@/common/polyOffice/PolyOfficeDialog.vue'
import PolyOfficeListItemImport from '@/common/polyOffice/PolyOfficeListItemImport.vue'
import SearchTextField from '@/common/SearchTextField.vue'
import { getRoomRouteType } from '@/common/utils/rooms'
import { toast } from '@/common/utils/toast'
import { RECENT_FOLDER, ROOT_FOLDER } from '@/project/documents/constants/special-folders'
import DocumentsExistingTreeDialog from '@/project/documents/dialogs/DocumentsExistingTreeDialog.vue'
import DocumentsHeaderFilters from '@/project/documents/DocumentsHeaderFilters.vue'
import { ROOM_SETTINGS_ROUTE_NAME } from '@/router/routerData'
import {
  DELETE_SEARCH_RESULTS,
  GET_NUMBERING,
  GET_PAGINATED_DOCUMENTS,
  GET_SEARCH_RESULTS,
  LOAD_FOLDERS_TREE,
  OPEN_SHARE_DOCUMENTS_INDEX_BY_EMAIL_PANE,
  PATCH_NUMBERING,
  POST_FILDERS_FROM_IMANAGE,
} from '@/store/modules/documents/action_types'
import {
  RESET_DOCUMENTS_PAGINATION,
  SET_FILTERS_PANE_IS_OPEN,
  SET_NUMBERING_SNACKBAR_IS_OPEN,
  SET_SEARCH_QUERY,
} from '@/store/modules/documents/mutation_types'
import { DocumentsCreateNewFolderActionTypes } from '@/store/modules/documents-create-new-folder/action_types'
import { OPEN_DOCUMENTS_UPLOAD_FILES } from '@/store/modules/documents-upload-files/action_types'
import { GET_IMPORT_IFRAME } from '@/store/modules/poly-office/action_types'

import DocumentsNumberingDisableDialog from './dialogs/DocumentsNumberingDisableDialog.vue'
import DocumentsActionBar from './DocumentsActionBar.vue'
import DocumentsBreadcrumb from './DocumentsBreadcrumb.vue'

export default {
  name: 'DocumentsHeader',
  setup () {
    const { smAndDown } = useDisplay()
    return { smAndDown }
  },
  components: {
    AppTooltip,
    AppDownloadFile,
    DocumentsBreadcrumb,
    DocumentsExistingTreeDialog,
    DocumentsHeaderFilters,
    DocumentsActionBar,
    DocumentsNumberingDisableDialog,
    PolyOfficeDialog,
    PolyOfficeListItemImport,
    SearchTextField,
    AppListDivider,
    OfficeIcon,
    IManageListItemImport,
    IManageFilePickerDialog,
    IManageFolderPickerDialog,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
    actionsDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      ROOT_FOLDER,
      ROOM_SETTINGS_ROUTE_NAME,
      searchQuery: '',
      showNumberingDisableDialog: false,
      existingTreeDialogIsOpen: false,
      iManageFilesPickerDialogIsOpen: false,
      iManageFoldersPickerDialogIsOpen: false,
    }
  },
  computed: {
    ...mapState('documents', [
      'selectedDocuments',
      'loading',
      'paginatedDocumentsFilters',
      'numberingEnabled',
      'searchModeEnabled',
      'patchNumberingPending',
      'paginatedDocuments',
      'paginatedDocumentsCurrentFolderId',
      'getFolderPathPending',
    ]),
    ...mapState('documentsBreadcrumb', ['breadcrumb']),
    ...mapState('room', ['currentRoom']),
    ...mapState('polyOffice', ['dialogIsOpen', 'dialogSuccess']),
    ...mapGetters('imanage', ['imanageEnabledAndLogged']),
    ...mapGetters('room', ['currentUserRights', 'isDataRoom', 'isStorageFull']),
    ...mapGetters('documentsBreadcrumb', [
      'hasTodoFolderOrChild',
      'isCurrentlyInTodoFolderRoot',
      'hasQaFolderOrChild',
    ]),
    ...mapGetters('user', ['polyOfficeEnabled']),
    computedRoomRouteType () {
      return getRoomRouteType(this.currentRoom)
    },
    exportIndexToExcelLink () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.mnemo}/downloads/documents-index-excel`
    },
    exportIndexToPdfLink () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.mnemo}/downloads/documents-index-pdf`
    },
    search: {
      get () {
        return this.searchQuery
      },
      set: debounce(function (val) {
        this.searchQuery = val
        this.searchDocs()
      }, 1000),
    },
    displayAddMenu () {
      return (
        this.paginatedDocumentsCurrentFolderId !== RECENT_FOLDER.id &&
        !this.isCurrentlyInTodoFolderRoot &&
        !this.hasQaFolderOrChild &&
        (
          this.currentRoom.isPM ||
          this.currentUserRights.canUpload ||
          this.currentUserRights.canCreateFolders
        )
      )
    },
    breadcrumbIsShown () {
      return this.breadcrumb.length >= 1 && !this.searchModeEnabled
    },
    filtersCount () {
      return this.paginatedDocumentsFilters.length
    },
    isImportFromPolyMenuDisplayed () {
      return this.polyOfficeEnabled &&
        ((this.currentRoom.isPM && !this.hasTodoFolderOrChild) ||
          (this.currentUserRights.canUpload && !this.isCurrentlyInTodoFolderRoot))
    },
    isImportExistingTreeMenuDisplayed () {
      return this.currentRoom.isPM && !this.hasTodoFolderOrChild
    },
    isExtraImportMenuDisplayed () {
      return this.isImportFromPolyMenuDisplayed || this.isImportExistingTreeMenuDisplayed
    },
    showSizeExceededTooltip () {
      return this.isStorageFull
    },
  },
  watch: {
    searchModeEnabled (searchModeIsEnabled) {
      if (!searchModeIsEnabled) {
        this.searchQuery = ''
      }
    },
  },
  mounted () {
    this.GET_NUMBERING(this.mnemo)
  },
  methods: {
    ...mapActions('documentsCreateNewFolder', [DocumentsCreateNewFolderActionTypes.OPEN_DOCUMENTS_CREATE_NEW_FOLDER]),
    ...mapActions('documentsUploadFiles', [OPEN_DOCUMENTS_UPLOAD_FILES]),
    ...mapActions('documents', [
      DELETE_SEARCH_RESULTS,
      GET_NUMBERING,
      GET_SEARCH_RESULTS,
      LOAD_FOLDERS_TREE,
      OPEN_SHARE_DOCUMENTS_INDEX_BY_EMAIL_PANE,
      PATCH_NUMBERING,
      GET_PAGINATED_DOCUMENTS,
      POST_FILDERS_FROM_IMANAGE,
    ]),
    ...mapActions('polyOffice', [
      GET_IMPORT_IFRAME,
    ]),
    ...mapMutations('documents', [SET_FILTERS_PANE_IS_OPEN, SET_NUMBERING_SNACKBAR_IS_OPEN, SET_SEARCH_QUERY, RESET_DOCUMENTS_PAGINATION]),
    searchDocs () {
      if (this.search) {
        this.SET_SEARCH_QUERY(this.searchQuery)
      } else {
        this.DELETE_SEARCH_RESULTS(null)
      }

      this.RESET_DOCUMENTS_PAGINATION()
      this.GET_PAGINATED_DOCUMENTS()
    },
    async toggleNumbering () {
      try {
        await this.PATCH_NUMBERING({ mnemo: this.mnemo, value: !this.numberingEnabled })
        await this.GET_NUMBERING(this.mnemo)

        this.LOAD_FOLDERS_TREE()
        await this.GET_PAGINATED_DOCUMENTS()

        if (this.numberingEnabled) {
          this.SET_NUMBERING_SNACKBAR_IS_OPEN(true)
        } else {
          this.SET_NUMBERING_SNACKBAR_IS_OPEN(false)
          toast.success(this.$t('project.documents.DocumentsHeader.successNumberingDisabled'))
        }
      } catch {
        toast.error(this.$t('project.documents.DocumentsHeader.errorNumbering'))
      }
    },
    confirmNumberingDisableDialog () {
      this.showNumberingDisableDialog = false
      this.toggleNumbering()
    },
    onNumberingChange () {
      if (this.numberingEnabled) {
        this.showNumberingDisableDialog = true
      } else {
        this.toggleNumbering()
      }
    },
    onOpenFiltersPane () {
      this.SET_FILTERS_PANE_IS_OPEN(true)
    },
    onSendDocumentsIndexByEmail () {
      this.OPEN_SHARE_DOCUMENTS_INDEX_BY_EMAIL_PANE()
    },
    async refreshDocuments () {
      try {
        await this.GET_PAGINATED_DOCUMENTS()
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.documents.DocumentsHeader.getDocumentsError'))
      }
    },
    async polyOfficeOpenDialogHandler () {
      const payload = new DocumentsImport(
        this.mnemo,
        this.paginatedDocumentsCurrentFolderId,
      )
      await this.GET_IMPORT_IFRAME({
        payload,
        successMessage: this.$t('project.documents.DocumentsHeader.polyOffice.importSuccessful'),
      })
    },
    polyOfficeDialogCallback (data) {
      if (data.status === POLY_OFFICE_DIALOG_STATUS_SUCCESS) {
        this.refreshDocuments()
        toast.success(this.dialogSuccess)
      }
    },
    openIManageFilesPicker () {
      this.iManageFilesPickerDialogIsOpen = true
    },
    openIManageFoldersPicker () {
      this.iManageFoldersPickerDialogIsOpen = true
    },
    onImanageFiles (files) {
      this.onImanageImport('files', files)
    },
    onImanageFolders (folders) {
      this.onImanageImport('folders', folders)
    },
    onImanageImport (type, result) {
      const data = {
        parentId: this.paginatedDocumentsCurrentFolderId,
        [type]: result.selected.split(','),
      }
      this.preparePostFildersFromIManage(data)
    },
    async preparePostFildersFromIManage (data) {
      try {
        await this.POST_FILDERS_FROM_IMANAGE({
          mnemo: this.mnemo,
          data,
        })
        toast.success(this.$t('common.imanage.IManageListItemImport.postFildersFromIManageSuccess'))
        this.refreshDocuments()
      } catch (error) {
        toast.error(this.$t('common.imanage.IManageListItemImport.postFildersFromIManageError'))
        console.error(error)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.header-container {
  display: unset;
}
.sticky-container {
  position: sticky;
  top: 0;
  z-index: 2;
}

.sticky-container .v-row {
  background-color: rgb(var(--v-theme-background));
}

:deep(.v-switch .v-input__control) {
  grid-area: auto;
}

.gap {
  gap: 16px;
}
@media (min-width: 960px) and (max-width: 980px) {
  .gap {
    gap: 8px;
  }
}
</style>
